// src/pages/User/Register.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { registerUser, verifyUser } from "../../services/api";
import SwiftAlert from "../../components/SwiftAlert";
import { useAuth } from "../../contexts/AuthContext";
import Logo2 from "../../assets/Logo2.png";

const Register = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // --- STATE ---
  const [step, setStep] = useState("register");
  const [isLoading, setIsLoading] = useState(false);

  // Visibility Toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Errors
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Field-level errors (client + server-friendly)
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [confirmError, setConfirmError] = useState("");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    verificationCode: "",
  });

  const inputRefs = useRef([]);

  // ✅ Redirect ONLY if AuthContext says user exists
  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, user, navigate]);

  // ============================================================
  // ✅ CLIENT VALIDATION HELPERS
  // ============================================================

  const isValidName = (value) => {
    const v = value.trim();
    if (!v) return false;
    if (v.length < 2) return false;
    if (v.length > 50) return false;
    return /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/.test(v);
  };

  const validateNameField = (label, value) => {
    const v = value.trim();
    if (!v) return `${label} is required.`;
    if (v.length < 2) return `${label} must be at least 2 characters.`;
    if (v.length > 50) return `${label} must be 50 characters or less.`;
    if (/\d/.test(v)) return `${label} cannot contain numbers.`;
    if (!/^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/.test(v)) {
      return `${label} can only contain letters, spaces, apostrophes (') and hyphens (-).`;
    }
    return "";
  };

  const validateEmailField = (value) => {
    const v = value.trim();
    if (!v) return "Email is required.";
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
    if (!ok) return "Please enter a valid email address.";
    return "";
  };

  // ============================================================
  // ✅ PASSWORD VALIDATION
  // ============================================================
  const password = formData.password;

  const validation = {
    length: password.length >= 12,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9\s]/.test(password),
  };

  const getMissingRequirements = () => {
    const missing = [];
    if (!validation.length) missing.push("12+ chars");
    if (!validation.uppercase) missing.push("1 Uppercase");
    if (!validation.number) missing.push("1 Number");
    if (!validation.special) missing.push("1 Special Char");
    return missing;
  };

  const missingReqs = getMissingRequirements();
  const isPasswordValid = missingReqs.length === 0;

  // --- INPUT HANDLER ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (error) setError("");

    if (name === "firstName") {
      const msg = validateNameField("First name", value);
      setFirstNameError(msg);
    }
    if (name === "lastName") {
      const msg = validateNameField("Last name", value);
      setLastNameError(msg);
    }
    if (name === "email") {
      const msg = validateEmailField(value);
      setEmailError(msg);
    }
    if (name === "password" || name === "confirmPassword") {
      setConfirmError("");
    }
  };

  // --- PIN INPUT HANDLERS ---
  const handlePinChange = (e, index) => {
    const val = e.target.value;
    if (!/^\d*$/.test(val)) return;

    const newDigit = val.slice(-1);
    const currentCode = formData.verificationCode.split("");
    while (currentCode.length < 6) currentCode.push("");
    currentCode[index] = newDigit;

    const newCode = currentCode.join("").slice(0, 6);
    setFormData((prev) => ({ ...prev, verificationCode: newCode }));

    if (newDigit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePinKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (!formData.verificationCode[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
        const currentCode = formData.verificationCode.split("");
        currentCode[index - 1] = "";
        setFormData((prev) => ({
          ...prev,
          verificationCode: currentCode.join(""),
        }));
      }
    }
  };

  const handlePinPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").slice(0, 6).split("");
    if (pasteData.every((char) => !isNaN(char))) {
      const newCode = [...pasteData, ...Array(6).fill("")].slice(0, 6).join("");
      setFormData((prev) => ({ ...prev, verificationCode: newCode }));
      const focusIndex = Math.min(pasteData.length, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  // --- SUBMIT: REGISTER ---
  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    setError("");
    setConfirmError("");

    const fnMsg = validateNameField("First name", formData.firstName);
    const lnMsg = validateNameField("Last name", formData.lastName);
    const emMsg = validateEmailField(formData.email);

    setFirstNameError(fnMsg);
    setLastNameError(lnMsg);
    setEmailError(emMsg);

    if (fnMsg || lnMsg || emMsg) {
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setConfirmError("Passwords do not match.");
      setIsLoading(false);
      return;
    }

    if (!isPasswordValid) {
      setConfirmError("Password does not meet all requirements.");
      setIsLoading(false);
      return;
    }

    try {
      await registerUser({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        password: formData.password,
      });
      setStep("verify");
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors && Array.isArray(data.errors)) {
        const emailErr = data.errors.find((x) => x.path === "email");
        if (emailErr) setEmailError(emailErr.msg);
        const passErr = data.errors.find((x) => x.path === "password");
        if (passErr) setConfirmError(passErr.msg);
        const firstErr = data.errors.find((x) => x.path === "firstName");
        if (firstErr) setFirstNameError(firstErr.msg);
        const lastErr = data.errors.find((x) => x.path === "lastName");
        if (lastErr) setLastNameError(lastErr.msg);

        if (!emailErr && !passErr && !firstErr && !lastErr) {
          setError(data.errors[0]?.msg || "Invalid input data");
        }
      } else if (data?.message) {
        const msg = data.message;
        if (msg.toLowerCase().includes("email")) setEmailError(msg);
        else setError(msg);
      } else {
        setError(err?.message || "Registration failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // --- SUBMIT: VERIFY ---
  const handleVerify = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      await verifyUser({
        email: formData.email.trim(),
        code: formData.verificationCode,
      });

      setSuccessMsg("Verification successful! Redirecting to login...");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Verification failed."
      );
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen flex items-center justify-center p-4 font-sans relative overflow-hidden">
      <style>{`
        input::-ms-reveal,
        input::-ms-clear {
          display: none;
        }
      `}</style>

      {error && (
        <SwiftAlert type="error" message={error} onClose={() => setError("")} />
      )}
      {successMsg && (
        <SwiftAlert
          type="success"
          message={successMsg}
          onClose={() => setSuccessMsg("")}
        />
      )}

      {/* Decorative Blobs */}
      <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-blue-400/20 rounded-full blur-3xl pointer-events-none md:hidden"></div>
      <div className="absolute bottom-[-10%] left-[-10%] w-96 h-96 bg-purple-400/20 rounded-full blur-3xl pointer-events-none md:hidden"></div>

      <div className="bg-white w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row border border-slate-100 md:min-h-[600px] z-10">
        
        {/* MOBILE HEADER */}
        <div className="md:hidden bg-blue-600 p-8 pb-12 text-white relative overflow-hidden">
          <div className="absolute top-[-20%] right-[-10%] w-40 h-40 bg-white/10 rounded-full blur-xl"></div>
          <div className="relative z-10">
            <Link to="/" className="text-white/80 hover:text-white flex items-center gap-2 text-sm font-medium mb-8">
              <i className="fa-solid fa-arrow-left"></i> Back to Home
            </Link>
            <h2 className="text-2xl font-bold leading-tight">
              Master your reviews<br />with Intelligent Design.
            </h2>
            <p className="text-blue-100 text-sm mt-1">
              Experience a smarter way to prepare for exams.
            </p>
          </div>
        </div>

        {/* DESKTOP SIDEBAR */}
        <div className="hidden md:flex md:w-1/2 bg-blue-600 p-12 flex-col justify-between text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-16 -mt-16 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-900 opacity-20 rounded-full -ml-10 -mb-10 blur-2xl"></div>

          <div className="relative z-10">
            <Link to="/" className="flex items-center gap-2 mb-8 hover:opacity-80 transition text-sm font-medium">
              <i className="fa-solid fa-arrow-left"></i> Back to Home
            </Link>
            
            <h2 className="text-3xl font-bold leading-tight mb-4">
              Master your reviews<br />with Intelligent Design.
            </h2>
            
            <p className="text-blue-100 text-sm leading-relaxed opacity-90">
              Experience a smarter way to prepare for exams. ReviewMate combines AI-driven insights with blockchain security to ensure your progress is always authentic and secure.
            </p>
          </div>

          {/* REVIEWER ICON SHOWCASE */}
          <div className="my-auto py-8 flex items-center justify-center relative z-10">
            <div className="relative">
              {/* Soft decorative glow */}
              <div className="absolute inset-0 bg-white/10 rounded-3xl blur-2xl transform scale-125"></div>

              {/* Main Reviewer Document Card */}
              <div className="w-32 h-32 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 flex items-center justify-center transform rotate-6 hover:rotate-0 transition duration-500 shadow-2xl group cursor-default">
                <i className="fa-solid fa-file-contract text-5xl text-white/90 group-hover:scale-110 transition duration-300"></i>
              </div>

              {/* Floating Satellite 1: AI Reviewer Sparkles */}
              <div className="absolute -top-3 -right-4 w-11 h-11 bg-white/15 backdrop-blur-md rounded-xl border border-white/20 flex items-center justify-center transform -rotate-12 hover:rotate-0 transition duration-300 shadow-lg group cursor-default">
                <i className="fa-solid fa-wand-magic-sparkles text-lg text-blue-200"></i>
              </div>

              {/* Floating Satellite 2: Active Scholar Mastery */}
              <div className="absolute -bottom-3 -left-4 w-11 h-11 bg-white/15 backdrop-blur-md rounded-xl border border-white/20 flex items-center justify-center transform -rotate-6 hover:rotate-0 transition duration-300 shadow-lg group cursor-default">
                <i className="fa-solid fa-graduation-cap text-lg text-blue-200"></i>
              </div>
            </div>
          </div>

          <div className="relative z-10 space-y-5 pb-6">
            <div className="flex items-center gap-4 text-sm font-semibold text-white">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <i className="fa-solid fa-robot text-lg"></i>
              </div>
              Automated AI Reviewers
            </div>
            <div className="flex items-center gap-4 text-sm font-semibold text-white">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <i className="fa-solid fa-layer-group text-lg"></i>
              </div>
              Blockchain Verification
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - FORM */}
        <div className="w-full md:w-1/2 px-6 py-8 md:p-12 flex flex-col justify-center relative bg-white">
          {step === "register" && (
            <div className="animate-[fadeIn_0.3s_ease-out]">
              {/* Toggle Switch */}
              <div className="flex bg-slate-100 p-1 rounded-xl mb-6">
                <Link
                  to="/login"
                  className="flex-1 py-2 text-sm font-semibold rounded-lg text-slate-500 hover:text-slate-800 text-center transition hover:bg-white/50"
                >
                  Log In
                </Link>
                <button
                  type="button"
                  className="flex-1 py-2 text-sm font-semibold rounded-lg bg-white shadow-sm text-slate-800 cursor-default ring-1 ring-black/5"
                >
                  Register
                </button>
              </div>

              <form onSubmit={handleRegister} className="space-y-5">
                <div className="flex gap-4">
                  {/* FIRST NAME */}
                  <div className="w-1/2">
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      placeholder="Juan"
                      value={formData.firstName}
                      className={`w-full px-4 py-3 rounded-lg border focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition text-sm text-slate-700 placeholder-slate-400 ${
                        firstNameError ? "border-red-500" : "border-slate-200"
                      }`}
                      required
                      onChange={handleInputChange}
                    />
                    {firstNameError && (
                      <p className="text-red-500 text-xs font-semibold mt-1 flex items-center gap-1 animate-pulse">
                        <i className="fa-solid fa-circle-exclamation"></i>{" "}
                        {firstNameError}
                      </p>
                    )}
                  </div>

                  {/* LAST NAME */}
                  <div className="w-1/2">
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Dela Cruz"
                      value={formData.lastName}
                      className={`w-full px-4 py-3 rounded-lg border focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition text-sm text-slate-700 placeholder-slate-400 ${
                        lastNameError ? "border-red-500" : "border-slate-200"
                      }`}
                      required
                      onChange={handleInputChange}
                    />
                    {lastNameError && (
                      <p className="text-red-500 text-xs font-semibold mt-1 flex items-center gap-1 animate-pulse">
                        <i className="fa-solid fa-circle-exclamation"></i>{" "}
                        {lastNameError}
                      </p>
                    )}
                  </div>
                </div>

                {/* EMAIL */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="student@university.edu"
                    value={formData.email}
                    className={`w-full px-4 py-3 rounded-lg border focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition text-sm text-slate-700 placeholder-slate-400 ${
                      emailError ? "border-red-500" : "border-slate-200"
                    }`}
                    required
                    onChange={handleInputChange}
                  />
                  {emailError && (
                    <p className="text-red-500 text-xs font-semibold mt-1 flex items-center gap-1 animate-pulse">
                      <i className="fa-solid fa-circle-exclamation"></i>{" "}
                      {emailError}
                    </p>
                  )}
                </div>

                {/* PASSWORD */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="••••••••"
                      value={formData.password}
                      className={`w-full px-4 py-3 pr-10 rounded-lg border focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition text-sm text-slate-700 placeholder-slate-400 ${
                        !isPasswordValid && formData.password.length > 0
                          ? "border-red-500"
                          : "border-slate-200"
                      }`}
                      required
                      onChange={handleInputChange}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition p-1 cursor-pointer z-10"
                      tabIndex="-1"
                    >
                      <i
                        className={`fa-solid ${
                          showPassword ? "fa-eye-slash" : "fa-eye"
                        }`}
                      ></i>
                    </button>
                  </div>

                  {!isPasswordValid && formData.password.length > 0 && (
                    <p className="text-red-500 text-xs font-semibold mt-1 flex items-start gap-1 animate-pulse">
                      <i className="fa-solid fa-circle-exclamation mt-0.5"></i>
                      <span>Missing: {missingReqs.join(", ")}</span>
                    </p>
                  )}
                </div>

                {/* CONFIRM PASSWORD */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirm ? "text" : "password"}
                      name="confirmPassword"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      className={`w-full px-4 py-3 pr-10 rounded-lg border focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition text-sm text-slate-700 placeholder-slate-400 ${
                        confirmError ? "border-red-500" : "border-slate-200"
                      }`}
                      required
                      onChange={handleInputChange}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition p-1 cursor-pointer z-10"
                      tabIndex="-1"
                    >
                      <i
                        className={`fa-solid ${
                          showConfirm ? "fa-eye-slash" : "fa-eye"
                        }`}
                      ></i>
                    </button>
                  </div>
                  {confirmError && (
                    <p className="text-red-500 text-xs font-semibold mt-1 flex items-center gap-1 animate-pulse">
                      <i className="fa-solid fa-circle-exclamation"></i>{" "}
                      {confirmError}
                    </p>
                  )}
                </div>

                {/* TERMS CHECKBOX */}
                <div className="flex items-center gap-3 py-1">
                  <input
                    type="checkbox"
                    id="terms"
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-600 cursor-pointer"
                    required
                  />
                  <label
                    htmlFor="terms"
                    className="text-xs text-slate-500 cursor-pointer select-none"
                  >
                    I agree to the{" "}
                    <Link to="/terms" className="text-blue-600 font-semibold hover:underline" target="_blank">
                      Terms
                    </Link>{" "}
                    and{" "}
                    <Link to="/privacy-policy" className="text-blue-600 font-semibold hover:underline" target="_blank">
                      Privacy Policy
                    </Link>
                    .
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition shadow-lg shadow-blue-600/20 mt-4 active:scale-[0.98] ${
                    isLoading ? "opacity-80 cursor-not-allowed" : ""
                  }`}
                >
                  {isLoading ? "Creating Account..." : "Create Account"}
                </button>
              </form>
            </div>
          )}

          {/* VERIFICATION STEP */}
          {step === "verify" && (
            <div className="flex flex-col items-center justify-center text-center animate-[fadeIn_0.5s_ease-out]">
              <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-6">
                <i className="fa-solid fa-envelope-open-text text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                Check your email
              </h3>
              <p className="text-sm text-slate-500 mb-8">
                We sent a 6-digit verification code to <br />
                <span className="font-bold text-slate-800">
                  {formData.email}
                </span>
              </p>

              <form onSubmit={handleVerify} className="w-full space-y-6">
                <div>
                  <div
                    className="flex gap-2 justify-center mb-4"
                    onPaste={handlePinPaste}
                  >
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <input
                        key={index}
                        ref={(el) => (inputRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="one-time-code"
                        maxLength="1"
                        value={formData.verificationCode[index] || ""}
                        onChange={(e) => handlePinChange(e, index)}
                        onKeyDown={(e) => handlePinKeyDown(e, index)}
                        className="w-10 h-12 sm:w-12 sm:h-14 text-center text-xl font-bold rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none transition text-slate-800 bg-slate-50 focus:bg-white"
                      />
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || successMsg}
                  className={`w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition shadow-lg shadow-blue-600/20 mt-4 active:scale-[0.98] ${
                    isLoading ? "opacity-80 cursor-not-allowed" : ""
                  }`}
                >
                  {isLoading ? "Verifying..." : "Verify & Continue"}
                </button>
              </form>

              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setStep("register")}
                  className="text-xs text-slate-400 hover:text-slate-600 underline transition"
                >
                  Wrong email address?
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;